import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect } from 'react';
import { AlertTriangle, Info } from 'lucide-react';
import Button from '../ui/Button';
/** Estilos visuais por nível (claro + dark com classes Tailwind) */
const estilosNivel = {
    info: {
        icone: Info,
        faixa: 'bg-icavi-500',
        borda: 'border-icavi-200 dark:border-icavi-800/80',
        card: 'bg-white dark:bg-gray-800',
        titulo: 'text-icavi-900 dark:text-icavi-100',
        rotulo: 'text-icavi-600 dark:text-icavi-400',
        iconeFundo: 'bg-icavi-100 text-icavi-600 dark:bg-icavi-950/80 dark:text-icavi-400',
        mensagem: 'text-gray-700 dark:text-gray-300',
        separador: 'border-gray-100 dark:border-gray-700',
        rodape: 'bg-gray-50/80 dark:bg-gray-900/80',
        botao: 'primary',
    },
    atencao: {
        icone: AlertTriangle,
        faixa: 'bg-amber-500',
        borda: 'border-amber-200 dark:border-amber-800/80',
        card: 'bg-white dark:bg-gray-800',
        titulo: 'text-amber-900 dark:text-amber-100',
        rotulo: 'text-amber-700 dark:text-amber-400',
        iconeFundo: 'bg-amber-100 text-amber-600 dark:bg-amber-950/80 dark:text-amber-400',
        mensagem: 'text-gray-700 dark:text-gray-300',
        separador: 'border-gray-100 dark:border-gray-700',
        rodape: 'bg-gray-50/80 dark:bg-gray-900/80',
        botao: 'warning',
    },
    urgente: {
        icone: AlertTriangle,
        faixa: 'bg-red-500',
        borda: 'border-red-200 dark:border-red-800/80',
        card: 'bg-white dark:bg-gray-800',
        titulo: 'text-red-900 dark:text-red-100',
        rotulo: 'text-red-600 dark:text-red-400',
        iconeFundo: 'bg-red-100 text-red-600 dark:bg-red-950/80 dark:text-red-400',
        mensagem: 'text-gray-700 dark:text-gray-300',
        separador: 'border-gray-100 dark:border-gray-700',
        rodape: 'bg-gray-50/80 dark:bg-gray-900/80',
        botao: 'danger',
    },
};
/**
 * Modal bloqueante de aviso do sistema.
 * Overlay com blur; card responsivo ao tema claro/escuro.
 */
const ModalAvisoSistema = ({ aberto, aviso, confirmando, onConfirmar, }) => {
    const nivel = aviso?.nivel ?? 'info';
    const estilo = estilosNivel[nivel] ?? estilosNivel.info;
    const IconeNivel = estilo.icone;
    // Foco inicial no botão de confirmação para acessibilidade
    useEffect(() => {
        if (!aberto || !aviso)
            return;
        const timer = window.setTimeout(() => {
            document.getElementById('aviso-sistema-confirmar-btn')?.focus();
        }, 120);
        return () => window.clearTimeout(timer);
    }, [aberto, aviso?.id]);
    if (!aberto || !aviso) {
        return null;
    }
    return (_jsxs("div", { className: "portal-aviso-sistema-root fixed inset-0 z-[9999] overflow-y-auto", "aria-hidden": false, children: [_jsx("div", { className: "portal-aviso-sistema-backdrop fixed inset-0 bg-black/55 dark:bg-black/65 backdrop-blur-md", "aria-hidden": "true" }), _jsx("div", { className: "flex min-h-full items-center justify-center p-4 sm:p-6", children: _jsxs("div", { role: "dialog", "aria-modal": "true", "aria-labelledby": "aviso-sistema-titulo", className: `portal-aviso-sistema-card relative z-10 w-full max-w-lg overflow-hidden rounded-2xl border shadow-2xl ring-1 ring-black/5 dark:ring-white/10 slide-up ${estilo.borda} ${estilo.card}`, children: [_jsx("div", { className: `h-1.5 w-full ${estilo.faixa}`, "aria-hidden": "true" }), _jsxs("div", { className: `flex items-start gap-4 border-b px-5 py-5 sm:px-6 ${estilo.separador}`, children: [_jsx("span", { className: `flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${estilo.iconeFundo}`, "aria-hidden": "true", children: _jsx(IconeNivel, { className: "h-5 w-5", strokeWidth: 2.25 }) }), _jsxs("div", { className: "min-w-0 flex-1 pt-0.5", children: [_jsx("p", { className: `text-xs font-semibold uppercase tracking-wider ${estilo.rotulo}`, children: "Aviso do sistema" }), _jsx("h2", { id: "aviso-sistema-titulo", className: `mt-1 text-lg font-bold leading-snug sm:text-xl ${estilo.titulo}`, children: aviso.titulo })] })] }), _jsx("div", { className: "max-h-[min(60vh,28rem)] overflow-y-auto px-5 py-5 sm:px-6", children: _jsx("p", { className: `whitespace-pre-wrap text-sm leading-relaxed sm:text-base ${estilo.mensagem}`, children: aviso.mensagem }) }), _jsx("div", { className: `flex justify-end border-t px-5 py-4 sm:px-6 ${estilo.separador} ${estilo.rodape}`, children: _jsx(Button, { id: "aviso-sistema-confirmar-btn", variant: estilo.botao, onClick: onConfirmar, loading: confirmando, disabled: confirmando, className: "min-w-[10.5rem] shadow-sm", children: "Li e entendi" }) })] }) })] }));
};
export default ModalAvisoSistema;
